import React, { useState, useEffect, useMemo } from 'react';
import { useParams, Link, useSearchParams, useNavigate } from 'react-router-dom';
import {
  ArrowLeft, Search, RefreshCw, Upload, Download, Trash2,
  SkipForward, Play, AlertCircle, ChevronUp, ChevronDown, ChevronRight, ChevronLeft, FileSearch, FileText
} from 'lucide-react';
import syncService from '../services/syncService';
import { useAuth } from '../contexts/AuthContext';
import { useAccount } from '../contexts/AccountContext';
import Card from './shared/Card';
import Button from './shared/Button';
import Spinner from './shared/Spinner';
import './TaskReconciliationPage.css';
import { getRecordStatusColor, getRecordStatusTitle, RECORD_STATUS_OPTIONS } from '../utils/recordStatus';
import { classifyStoryLine, commentWithAnnotations, hiddenHistoryMatches } from './shared/messageLogUtils';
import LinkifiedText from './shared/LinkifiedText';

// Simple Checkbox Component
function Checkbox({ checked, onChange, disabled, ariaLabel }) {
  return (
    <input
      type="checkbox"
      checked={checked}
      onChange={onChange}
      disabled={disabled}
      aria-label={ariaLabel}
      className="checkbox-input"
    />
  );
}

function TaskReconciliationPage() {
  const { syncId, taskId } = useParams();
  const navigate = useNavigate();
  const [searchParams] = useSearchParams();
  const fromTab = searchParams.get('from') || 'configuration';
  const { user, isSuperAdmin } = useAuth();
  const { selectedAccount } = useAccount();

  // Project/Task data
  const [projectName, setProjectName] = useState('');
  const [taskName, setTaskName] = useState('');

  // Records state
  const [allRecords, setAllRecords] = useState([]);
  const [loading, setLoading] = useState(false);
  const [loadingMore, setLoadingMore] = useState(false);
  const [error, setError] = useState(null);
  const [hasSearched, setHasSearched] = useState(false);
  const [nextContinuationToken, setNextContinuationToken] = useState(null);

  // Filter state — pre-populate from URL query params when navigating from summary row
  const [scopeType, setScopeType] = useState(() => searchParams.get('scopeType') || '');
  const [recordStatus, setRecordStatus] = useState(() => searchParams.get('status') || '');
  const [sourceRecordId, setSourceRecordId] = useState('');
  const [targetRecordId, setTargetRecordId] = useState('');
  const [errorMessage, setErrorMessage] = useState('');
  const [showOnlyNullTargetId, setShowOnlyNullTargetId] = useState(false);

  // Selection state
  const [selectedRecords, setSelectedRecords] = useState(new Set());

  // Pagination state
  const [page, setPage] = useState(1);
  const [pageSize] = useState(25);

  // Sorting state
  const [sortBy, setSortBy] = useState('lastSyncTime');
  const [sortOrder, setSortOrder] = useState('desc');

  // Bulk actions state
  const [processingBulkAction, setProcessingBulkAction] = useState(false);
  const [showConfirmModal, setShowConfirmModal] = useState(false);
  const [pendingAction, setPendingAction] = useState(null);
  const [bulkActionResult, setBulkActionResult] = useState(null);
  const [showSuccessModal, setShowSuccessModal] = useState(false);

  // Delete state
  const [showDeleteModal, setShowDeleteModal] = useState(false);
  const [recordsToDelete, setRecordsToDelete] = useState([]);
  const [deleting, setDeleting] = useState(false);

  // CSV Upload
  const [showCSVUploadModal, setShowCSVUploadModal] = useState(false);

  // Toast
  const [toast, setToast] = useState(null);

  useEffect(() => {
    if (syncId && taskId && selectedAccount) {
      loadProjectAndTask();
      // Auto-populate the list on entry: run a search using the URL query params if we arrived from a
      // summary row, otherwise an unfiltered search so the screen is filled without a manual Search.
      fetchRecords({
        scopeType: searchParams.get('scopeType') || undefined,
        recordStatus: searchParams.get('status') || undefined,
      });
    }
  }, [syncId, taskId, selectedAccount]);

  const loadProjectAndTask = async () => {
    try {
      const projectData = await syncService.getSyncProjectDetails(syncId);
      setProjectName(projectData.projectName || 'Project');

      const taskData = await syncService.getTaskDetails(syncId, taskId);
      setTaskName(taskData.taskName || 'Task');
    } catch (err) {
      console.error('Error loading project/task:', err);
    }
  };

  const fetchRecords = async (customFilters = null) => {
    try {
      setLoading(true);
      setError(null);

      const filters = customFilters || {
        scopeType: scopeType || undefined,
        recordStatus: recordStatus || undefined,
        sourceRecordId: sourceRecordId || undefined,
        targetRecordId: targetRecordId || undefined,
        errorMessage: errorMessage || undefined,
        showOnlyNullTargetId: showOnlyNullTargetId || undefined
      };

      const result = await syncService.searchReconciliationRecords(syncId, taskId, filters);
      const records = Array.isArray(result) ? result : (result?.records || []);
      setAllRecords(records);
      setNextContinuationToken(null);
      setHasSearched(true);
      setPage(1); // Reset to first page
    } catch (err) {
      console.error('Error fetching records:', err);
      setError(err.response?.data?.message || err.message || 'Failed to load reconciliation records');
      setAllRecords([]);
      setNextContinuationToken(null);
    } finally {
      setLoading(false);
    }
  };

  const handleSearch = () => {
    fetchRecords();
  };

  // Open the record's message log — a full page (STYLE.md: detail views are pages, not modals).
  // The current filters travel in location.state so the page's Prev/Next walks the same list.
  const handleRowClick = (record) => {
    if (!record.sprRecordUuid) return;
    navigate(`/sync/${syncId}/task/${taskId}/reconciliation/${record.sprRecordUuid}/log`, {
      state: {
        backTo: `${window.location.pathname}${window.location.search}`,
        filters: {
          scopeType: scopeType || undefined,
          recordStatus: recordStatus || undefined,
          sourceRecordId: sourceRecordId || undefined,
          targetRecordId: targetRecordId || undefined,
          errorMessage: errorMessage || undefined,
          showOnlyNullTargetId: showOnlyNullTargetId || undefined,
        },
      },
    });
  };

  // Open the CloudWatch Logs search in a new tab, pre-filtered to a source/target record id.
  const openCwLogsForRecord = (e, recordId) => {
    e.stopPropagation();
    if (!recordId) return;
    window.open(`/admin/opensearch/cwlogs?q=${encodeURIComponent(recordId)}`, '_blank', 'noopener');
  };

  const handleReset = () => {
    setScopeType('');
    setRecordStatus('');
    setSourceRecordId('');
    setTargetRecordId('');
    setErrorMessage('');
    setShowOnlyNullTargetId(false);
    setAllRecords([]);
    setHasSearched(false);
    setError(null);
    setSelectedRecords(new Set());
    setNextContinuationToken(null);
    setPage(1);
  };

  const handleLoadMore = async () => {
    if (!nextContinuationToken) return;
    try {
      setLoadingMore(true);
      const filters = {
        scopeType: scopeType || undefined,
        recordStatus: recordStatus || undefined,
        sourceRecordId: sourceRecordId || undefined,
        targetRecordId: targetRecordId || undefined,
        errorMessage: errorMessage || undefined,
        showOnlyNullTargetId: showOnlyNullTargetId || undefined,
        continuationToken: nextContinuationToken,
      };
      const result = await syncService.searchReconciliationRecords(syncId, taskId, filters);
      const more = Array.isArray(result) ? result : (result?.records || []);
      setAllRecords(prev => [...prev, ...more]);
      setNextContinuationToken(result?.nextContinuationToken || null);
    } catch (err) {
      console.error('Error loading more records:', err);
    } finally {
      setLoadingMore(false);
    }
  };

  const handleSort = (column) => {
    if (sortBy === column) {
      setSortOrder(sortOrder === 'asc' ? 'desc' : 'asc');
    } else {
      setSortBy(column);
      setSortOrder('asc');
    }
  };

  const toggleSelectRecord = (recordId) => {
    setSelectedRecords(prev => {
      const newSet = new Set(prev);
      if (newSet.has(recordId)) {
        newSet.delete(recordId);
      } else {
        newSet.add(recordId);
      }
      return newSet;
    });
  };

  const toggleSelectAll = () => {
    const selectableRecordsOnPage = paginatedRecords.filter(r => r.recordid && r.recordid.trim());
    
    if (allCurrentPageSelected) {
      // Deselect all on current page
      setSelectedRecords(prev => {
        const newSet = new Set(prev);
        selectableRecordsOnPage.forEach(r => newSet.delete(r.recordid));
        return newSet;
      });
    } else {
      // Select all on current page
      setSelectedRecords(prev => {
        const newSet = new Set(prev);
        selectableRecordsOnPage.forEach(r => newSet.add(r.recordid));
        return newSet;
      });
    }
  };

  const handleBulkAction = (action) => {
    if (selectedRecords.size === 0) return;
    setPendingAction(action);
    setShowConfirmModal(true);
  };

  const handleConfirmAction = async () => {
    if (!pendingAction || selectedRecords.size === 0) return;

    try {
      setProcessingBulkAction(true);
      setShowConfirmModal(false);

      const recordIds = Array.from(selectedRecords);
      await syncService.bulkActionRecords(syncId, taskId, pendingAction, recordIds);

      setBulkActionResult({
        count: recordIds.length,
        actionType: pendingAction
      });
      setShowSuccessModal(true);

      setSelectedRecords(new Set());
      setPendingAction(null);

      // Refresh records
      await fetchRecords();
    } catch (err) {
      console.error('Error performing bulk action:', err);
      showToast(err.response?.data?.message || 'Failed to perform bulk action', 'error');
    } finally {
      setProcessingBulkAction(false);
    }
  };

  const handleCancelAction = () => {
    setShowConfirmModal(false);
    setPendingAction(null);
  };

  const handleCloseSuccessModal = () => {
    setShowSuccessModal(false);
    setBulkActionResult(null);
  };

  const handleDeleteClick = () => {
    const deletableSelectedRecords = selectedRecordsArray;
    if (deletableSelectedRecords.length === 0) return;
    setRecordsToDelete(deletableSelectedRecords);
    setShowDeleteModal(true);
  };

  const handleDeleteConfirm = async () => {
    if (!syncId || !taskId || recordsToDelete.length === 0) return;

    try {
      setDeleting(true);
      const recordUuids = recordsToDelete.map(record => record.recordUuid || record.recordid);

      await syncService.deleteReconciliationRecords(syncId, taskId, recordUuids);

      showToast(`${recordsToDelete.length} record(s) deleted successfully`, 'success');

      setShowDeleteModal(false);
      setRecordsToDelete([]);
      setSelectedRecords(new Set());

      await fetchRecords();
    } catch (err) {
      console.error('Error deleting records:', err);
      const errorMsg = err.response?.data?.message || err.message || 'Failed to delete records';
      showToast(errorMsg, 'error');
    } finally {
      setDeleting(false);
    }
  };

  const handleDeleteCancel = () => {
    setShowDeleteModal(false);
    setRecordsToDelete([]);
  };

  const handleCSVUploadSuccess = async () => {
    showToast('Reconciliation records uploaded successfully. Refreshing...', 'success');

    setScopeType('');
    setRecordStatus('');
    setSourceRecordId('');
    setTargetRecordId('');
    setErrorMessage('');
    setSelectedRecords(new Set());

    await new Promise(resolve => setTimeout(resolve, 1000));

    try {
      await fetchRecords({
        scopeType: undefined,
        recordStatus: undefined,
        sourceRecordId: undefined,
        targetRecordId: undefined,
      });
    } catch (error) {
      console.error('Failed to refresh after upload:', error);
      showToast('Upload successful, but failed to refresh. Click Search to view records.', 'error');
    }
  };

  const handleDownloadCSV = async () => {
    try {
      const filters = {
        scopeType: scopeType || undefined,
        recordStatus: recordStatus || undefined,
        sourceRecordId: sourceRecordId || undefined,
        targetRecordId: targetRecordId || undefined,
        errorMessage: errorMessage || undefined,
        showOnlyNullTargetId: showOnlyNullTargetId || undefined
      };

      const blob = await syncService.downloadReconciliationCSV(syncId, taskId, filters);
      const url = window.URL.createObjectURL(blob);
      const a = document.createElement('a');
      a.href = url;
      a.download = `reconciliation-${taskId}.csv`;
      document.body.appendChild(a);
      a.click();
      window.URL.revokeObjectURL(url);
      document.body.removeChild(a);
      showToast('Reconciliation records downloaded successfully', 'success');
    } catch (err) {
      console.error('Error downloading CSV:', err);
      showToast('Failed to download reconciliation records', 'error');
    }
  };

  const showToast = (message, type = 'info') => {
    setToast({ message, type });
    setTimeout(() => setToast(null), 5000);
  };

  // Shared with TaskConfigurationPage and MessageLogPage — see utils/recordStatus.js. This copy also
  // silently lacked DLQ, so a dead-lettered record showed grey here and red there.
  const getStatusColor = getRecordStatusColor;

  // IP PROTECTION. The comment carries the OHIP call story, and a SUCCEEDED call is our implementation
  // rather than the customer's data — together those lines spell out the exact request sequence we make
  // against the target. Strip them for everyone but a superadmin. Errors, warnings and plain notes stay:
  // those describe what happened to the customer's record and are theirs to act on.
  //
  // Applied here as well as in TaskConfigurationPage because this screen renders the comment as RAW
  // TEXT rather than as classified story steps — the same lines, leaking by a different route.
  const visibleComment = (comment) => {
    if (!comment) return comment;
    if (isSuperAdmin) return comment;
    const kept = String(comment).split('\n').filter(l => classifyStoryLine(l) !== 'success');
    return kept.join('\n').trim();
  };

  const renderSortIcon = (column) => {
    if (sortBy !== column) return null;
    return sortOrder === 'asc' ? (
      <ChevronUp className="w-4 h-4 inline ml-1" />
    ) : (
      <ChevronDown className="w-4 h-4 inline ml-1" />
    );
  };

  // Filter records based on showOnlyNullTargetId
  const filteredRecords = useMemo(() => {
    if (!showOnlyNullTargetId) return allRecords;
    return allRecords.filter(r => !r.targetRecordId || r.targetRecordId.trim() === '');
  }, [allRecords, showOnlyNullTargetId]);

  // Sorted records
  const sortedRecords = useMemo(() => {
    const sorted = [...filteredRecords];
    
    sorted.sort((a, b) => {
      let aVal = a[sortBy];
      let bVal = b[sortBy];

      // Handle null/undefined
      if (!aVal && !bVal) return 0;
      if (!aVal) return 1;
      if (!bVal) return -1;

      // Handle dates
      if (sortBy === 'lastSyncTime') {
        aVal = new Date(aVal).getTime();
        bVal = new Date(bVal).getTime();
      }

      // Compare
      if (aVal < bVal) return sortOrder === 'asc' ? -1 : 1;
      if (aVal > bVal) return sortOrder === 'asc' ? 1 : -1;
      return 0;
    });

    return sorted;
  }, [filteredRecords, sortBy, sortOrder]);

  // Paginated records
  const totalRecords = sortedRecords.length;
  const totalPages = Math.ceil(totalRecords / pageSize);
  const paginatedRecords = useMemo(() => {
    const start = (page - 1) * pageSize;
    const end = start + pageSize;
    return sortedRecords.slice(start, end);
  }, [sortedRecords, page, pageSize]);

  const selectableRecordsOnPage = paginatedRecords.filter(r => r.recordid && r.recordid.trim());
  const allCurrentPageSelected =
    selectableRecordsOnPage.length > 0 &&
    selectableRecordsOnPage.every((r) => selectedRecords.has(r.recordid));

  const selectedRecordsArray = useMemo(() => {
    return allRecords.filter((r) => selectedRecords.has(r.recordid));
  }, [allRecords, selectedRecords]);

  // Any selected record can be deleted — delete only removes the SPR tracking entry, not the synced
  // target data, so mapped/errored/duplicate records can be cleaned up too.
  const deletableSelectedRecords = useMemo(() => {
    return selectedRecordsArray;
  }, [selectedRecordsArray]);


  return (
    <div className="reconciliation-page">
      <div className="reconciliation-container">
        {/* Back Link */}
        <div className="back-link-section">
          <Link
            to={`/sync/${syncId}/task/${taskId}/configure?from=${fromTab}`}
            className="back-link"
          >
            <ArrowLeft className="w-4 h-4 mr-2" />
            Back to Task Configuration
          </Link>
          <div className="reconciliation-title-row">
            <div>
              <h1 className="page-title">Task Reconciliation</h1>
              <p className="page-description">Search and manage task records by status</p>
            </div>
            {isSuperAdmin && (
              <Link
                to={`/sync/${syncId}/task/${taskId}/report${scopeType ? `?scopeType=${encodeURIComponent(scopeType)}` : ''}`}
                className="reconciliation-report-btn"
                title="Presentable overview of all synced records with follow-up actions (superadmin)"
              >
                <FileText className="w-4 h-4" />
                Report
              </Link>
            )}
          </div>
        </div>

        {/* Search Filters Card */}
        <Card className="filters-card">
          <div className="filters-header">
            <h2 className="filters-title">Search Filters</h2>
            <div className="filters-actions">
              <Button variant="secondary" onClick={handleDownloadCSV} disabled={filteredRecords.length === 0}>
                <Download className="icon-sm" />
                Download CSV
              </Button>
              <Button variant="secondary" onClick={() => setShowCSVUploadModal(true)}>
                <Upload className="icon-sm" />
                Bulk Upload CSV
              </Button>
            </div>
          </div>

          {error && (
            <div className="error-alert">
              <div className="error-alert-content">
                <AlertCircle className="error-alert-icon" />
                <div>
                  <h3 className="error-alert-title">Search Failed</h3>
                  <p className="error-alert-message">{error}</p>
                </div>
              </div>
            </div>
          )}

          <div className="filters-grid">
            <div className="filter-field">
              <label className="filter-label">Record Status</label>
              <select
                value={recordStatus}
                onChange={(e) => setRecordStatus(e.target.value)}
                className="filter-input"
              >
                {/* Same set as the task Reconciliation panel — IN-FLIGHT means "not in a terminal
                    state" (what the summary counts), and the statuses nothing ever writes are gone. */}
                {RECORD_STATUS_OPTIONS.map(o => (
                  <option key={o.value} value={o.value}>{o.label}</option>
                ))}
              </select>
            </div>

            <div className="filter-field">
              <label className="filter-label">Source Record ID</label>
              <input
                type="text"
                value={sourceRecordId}
                onChange={(e) => setSourceRecordId(e.target.value)}
                onKeyDown={(e) => e.key === 'Enter' && handleSearch()}
                placeholder="Search by source record ID..."
                className="filter-input"
              />
            </div>

            <div className="filter-field">
              <label className="filter-label">Target Record ID</label>
              <input
                type="text"
                value={targetRecordId}
                onChange={(e) => setTargetRecordId(e.target.value)}
                onKeyDown={(e) => e.key === 'Enter' && handleSearch()}
                placeholder="Search by target record ID..."
                className="filter-input"
              />
            </div>

            <div className="filter-field">
              <label className="filter-label">Comment</label>
              <input
                type="text"
                value={errorMessage}
                onChange={(e) => setErrorMessage(e.target.value)}
                onKeyDown={(e) => e.key === 'Enter' && handleSearch()}
                placeholder="Search by comment..."
                className="filter-input"
              />
            </div>
          </div>

          <div className="filter-checkbox-section">
            <label className="filter-checkbox-label">
              <Checkbox
                checked={showOnlyNullTargetId}
                onChange={() => setShowOnlyNullTargetId(!showOnlyNullTargetId)}
                ariaLabel="Show only records with null target record ID"
              />
              <span>Show only records with no target record ID</span>
            </label>
          </div>

          <div className="filters-buttons">
            <Button variant="primary" onClick={handleSearch} disabled={loading}>
              {loading ? (
                <>
                  <Spinner size="sm" className="icon-sm" />
                  Searching...
                </>
              ) : (
                <>
                  <Search className="icon-sm" />
                  Search
                </>
              )}
            </Button>
            <Button variant="secondary" onClick={handleReset} disabled={loading}>
              <RefreshCw className="icon-sm" />
              Reset
            </Button>
          </div>
        </Card>

        {/* Selection Actions Bar */}
        {totalRecords > 0 && (
          <div className={`selection-bar ${selectedRecords.size > 0 ? 'selection-bar-active' : ''}`}>
            <p className="selection-text">
              {selectedRecords.size} of {totalRecords} record(s) selected
            </p>
            <div className="selection-actions">
              <Button
                variant="secondary"
                onClick={handleDeleteClick}
                disabled={deleting || deletableSelectedRecords.length === 0}
                className={deletableSelectedRecords.length === 0 ? 'opacity-50 cursor-not-allowed' : ''}
                title={
                  selectedRecords.size > 0 && deletableSelectedRecords.length === 0
                    ? 'Selected records cannot be deleted (they have a target record ID)'
                    : ''
                }
              >
                <Trash2 className="icon-sm" />
                Delete Selected
              </Button>
              <Button
                variant="secondary"
                onClick={() => handleBulkAction('skip')}
                disabled={processingBulkAction || selectedRecords.size === 0}
              >
                <SkipForward className="icon-sm" />
                Skip Selected
              </Button>
              <Button
                variant="primary"
                onClick={() => handleBulkAction('reprocess')}
                disabled={processingBulkAction || selectedRecords.size === 0}
              >
                <Play className="icon-sm" />
                Reprocess Selected
              </Button>
            </div>
          </div>
        )}

        {/* Records Table */}
        <Card className="table-card">
          <div className="table-wrapper">
            <table className="records-table">
              <thead>
                <tr>
                  <th className="checkbox-cell">
                    <Checkbox
                      checked={allCurrentPageSelected}
                      onChange={toggleSelectAll}
                      ariaLabel="Select all records on current page"
                    />
                  </th>
                  <th className="sortable-header" onClick={() => handleSort('sourcerecordid')}>
                    Source Record ID {renderSortIcon('sourcerecordid')}
                  </th>
                  <th className="sortable-header" onClick={() => handleSort('targetRecordId')}>
                    Target Record ID {renderSortIcon('targetRecordId')}
                  </th>
                  <th className="sortable-header" onClick={() => handleSort('recordStatus')}>
                    Status {renderSortIcon('recordStatus')}
                  </th>
                  <th className="sortable-header" onClick={() => handleSort('stage')}>
                    Stage {renderSortIcon('stage')}
                  </th>
                  <th className="sortable-header error-header" onClick={() => handleSort('comment')}>Comment {renderSortIcon('comment')}</th>
                  <th className="sortable-header" onClick={() => handleSort('lastSyncTime')}>
                    Last Sync Time {renderSortIcon('lastSyncTime')}
                  </th>
                </tr>
              </thead>
              <tbody>
                {paginatedRecords.length === 0 ? (
                  <tr>
                    <td colSpan={7} className="empty-cell">
                      <AlertCircle className="empty-icon" />
                      {!hasSearched ? (
                        <>
                          <p className="empty-title">Ready to Search</p>
                          <p className="empty-subtitle">
                            Configure your search criteria above and click Search to view reconciliation records
                          </p>
                        </>
                      ) : (
                        <>
                          <p className="empty-message">No records found matching your search criteria</p>
                          <Button variant="secondary" onClick={handleReset} className="empty-button">
                            Clear Filters
                          </Button>
                        </>
                      )}
                    </td>
                  </tr>
                ) : (
                  paginatedRecords.map((record, index) => {
                    const hasValidRecordId = Boolean(record.recordid && record.recordid.trim());
                    const isSelected = hasValidRecordId && selectedRecords.has(record.recordid);
                    const uniqueKey = record.recordid || `${record.sourcerecordid}-${index}`;

                    return (
                      <tr
                        key={uniqueKey}
                        className={`record-row ${isSelected ? 'record-row-selected' : ''} ${record.sprRecordUuid ? 'record-row-clickable' : ''}`}
                        onClick={(e) => {
                          if (e.target.type === 'checkbox') return;
                          handleRowClick(record);
                        }}
                        title={record.sprRecordUuid ? 'Click to view message log' : ''}
                      >
                        <td className="checkbox-cell">
                          <div title={!hasValidRecordId ? 'Cannot select: Record missing UUID from API' : ''}>
                            <Checkbox
                              checked={isSelected}
                              onChange={() => hasValidRecordId && toggleSelectRecord(record.recordid)}
                              disabled={!hasValidRecordId}
                              ariaLabel={`Select record ${record.sourcerecordid}`}
                            />
                          </div>
                        </td>
                        <td className="record-cell">
                          <span className="record-id-wrap">
                            {record.sourcerecordid || '-'}
                            {isSuperAdmin && record.sourcerecordid && (
                              <button
                                type="button"
                                className="cwlogs-btn"
                                title="Search Cloudwatch logs for this record"
                                onClick={(e) => openCwLogsForRecord(e, record.sourcerecordid)}
                              >
                                <FileSearch size={14} />
                              </button>
                            )}
                          </span>
                        </td>
                        <td className="record-cell">
                          <span className="record-id-wrap">
                            {record.targetRecordId || '-'}
                            {isSuperAdmin && record.targetRecordId && (
                              <button
                                type="button"
                                className="cwlogs-btn"
                                title="Search Cloudwatch logs for this record"
                                onClick={(e) => openCwLogsForRecord(e, record.targetRecordId)}
                              >
                                <FileSearch size={14} />
                              </button>
                            )}
                          </span>
                        </td>
                        <td className="status-cell">
                          <span className={`status-badge ${getStatusColor(record.recordStatus)}`} title={getRecordStatusTitle(record.recordStatus)}>
                            {record.recordStatus || '-'}
                          </span>
                          {isSuperAdmin && record.jiraKey && (
                            <a
                              className="recon-jira-chip sa-only"
                              href={`https://hconnect.atlassian.net/browse/${record.jiraKey}`}
                              target="_blank"
                              rel="noreferrer"
                              title="Jira ticket for this record"
                              onClick={(e) => e.stopPropagation()}
                            >
                              {record.jiraKey}
                            </a>
                          )}
                        </td>
                        <td className="record-cell">
                          {record.stage || '-'}
                        </td>
                        <td className="error-cell">
                          {/* The comment, plus the earlier history line a search matched on (HIL-89).
                              This screen's Comment box searches the record's whole history, so a hit
                              can be on text the row does not otherwise show. */}
                          {(() => {
                            const shown = visibleComment(commentWithAnnotations(record));
                            const matched = hiddenHistoryMatches(record, errorMessage);
                            if (!shown && !matched.length) return <span className="empty-value">-</span>;
                            return (
                              <>
                                {shown && (
                                  <div className="error-message" title={shown}>
                                    <LinkifiedText text={shown} />
                                  </div>
                                )}
                                {matched.map((line, i) => (
                                  <div className="recon-matched-history" key={i} title="An earlier comment on this record — this is what your search matched">
                                    <span className="recon-matched-history-label">Earlier</span>
                                    {line}
                                  </div>
                                ))}
                              </>
                            );
                          })()}
                        </td>
                        <td className="date-cell">
                          {record.lastSyncTime
                            ? new Date(record.lastSyncTime).toLocaleString()
                            : '-'}
                        </td>
                      </tr>
                    );
                  })
                )}
              </tbody>
            </table>
          </div>

          {/* Load More */}
          {nextContinuationToken && (
            <div className="load-more-container">
              <Button variant="secondary" onClick={handleLoadMore} disabled={loadingMore}>
                {loadingMore ? 'Loading...' : `Load More (${allRecords.length} loaded)`}
              </Button>
            </div>
          )}

          {/* Pagination */}
          {totalPages > 1 && (
            <div className="pagination">
              <div className="pagination-info">
                Showing {(page - 1) * pageSize + 1} to {Math.min(page * pageSize, totalRecords)} of{' '}
                {totalRecords} results
              </div>
              <div className="pagination-controls">
                <Button
                  variant="secondary"
                  onClick={() => setPage((p) => Math.max(1, p - 1))}
                  disabled={page === 1}
                >
                  Previous
                </Button>
                <div className="pagination-page">
                  Page {page} of {totalPages}
                </div>
                <Button
                  variant="secondary"
                  onClick={() => setPage((p) => Math.min(totalPages, p + 1))}
                  disabled={page === totalPages}
                >
                  Next
                </Button>
              </div>
            </div>
          )}
        </Card>
      </div>

      {/* Toast */}
      {toast && (
        <div className={`toast toast-${toast.type}`}>
          <div className="toast-content">
            {toast.type === 'success' && <AlertCircle className="toast-icon" />}
            {toast.type === 'error' && <AlertCircle className="toast-icon" />}
            <span>{toast.message}</span>
          </div>
          <button className="toast-close" onClick={() => setToast(null)}>×</button>
        </div>
      )}

      {/* Modals */}
      {showConfirmModal && (
        <BulkActionConfirmModal
          isOpen={showConfirmModal}
          onClose={handleCancelAction}
          onConfirm={handleConfirmAction}
          recordCount={selectedRecords.size}
          actionType={pendingAction || 'reprocess'}
        />
      )}

      {bulkActionResult && (
        <BulkSuccessModal
          isOpen={showSuccessModal}
          onClose={handleCloseSuccessModal}
          recordCount={bulkActionResult.count}
          actionType={bulkActionResult.actionType}
        />
      )}

      {showCSVUploadModal && (
        <ReconciliationCSVUploadModal
          isOpen={showCSVUploadModal}
          onClose={() => setShowCSVUploadModal(false)}
          syncId={syncId}
          taskId={taskId}
          onUploadSuccess={handleCSVUploadSuccess}
        />
      )}

      {showDeleteModal && (
        <ReconciliationDeleteModal
          isOpen={showDeleteModal}
          onClose={handleDeleteCancel}
          onConfirm={handleDeleteConfirm}
          recordCount={recordsToDelete.length}
          deleting={deleting}
        />
      )}

      {/* Message Log Panel */}
    </div>
  );
}

// Modal Components
function BulkActionConfirmModal({ isOpen, onClose, onConfirm, recordCount, actionType }) {
  if (!isOpen) return null;

  return (
    <div className="modal-overlay" onClick={onClose}>
      <div className="modal-content modal-sm" onClick={(e) => e.stopPropagation()}>
        <div className="modal-header">
          <h2>Confirm Bulk Action</h2>
          <button className="modal-close" onClick={onClose}>×</button>
        </div>
        <div className="modal-body">
          <p>
            Are you sure you want to <strong>{actionType}</strong> {recordCount} record(s)?
          </p>
          <p className="text-muted">This action cannot be undone.</p>
        </div>
        <div className="modal-footer">
          <Button variant="secondary" onClick={onClose}>Cancel</Button>
          <Button onClick={onConfirm}>
            {actionType === 'skip' ? 'Skip Records' : 'Reprocess Records'}
          </Button>
        </div>
      </div>
    </div>
  );
}

function BulkSuccessModal({ isOpen, onClose, recordCount, actionType }) {
  if (!isOpen) return null;

  return (
    <div className="modal-overlay" onClick={onClose}>
      <div className="modal-content modal-sm" onClick={(e) => e.stopPropagation()}>
        <div className="modal-header">
          <h2>Action Successful</h2>
          <button className="modal-close" onClick={onClose}>×</button>
        </div>
        <div className="modal-body">
          <p>
            Successfully {actionType === 'skip' ? 'skipped' : 'reprocessed'} {recordCount} record(s).
          </p>
        </div>
        <div className="modal-footer">
          <Button onClick={onClose}>Close</Button>
        </div>
      </div>
    </div>
  );
}

function ReconciliationCSVUploadModal({ isOpen, onClose, syncId, taskId, onUploadSuccess }) {
  const [file, setFile] = useState(null);
  const [uploading, setUploading] = useState(false);
  const [error, setError] = useState(null);

  const handleSubmit = async (e) => {
    e.preventDefault();
    
    if (!file) return;

    try {
      setUploading(true);
      setError(null);
      const result = await syncService.uploadReconciliationCSV(syncId, taskId, file);
      // The endpoint returns 200 even when it inserts nothing (unmatched columns, unresolved
      // task scope). Don't report success in that case — keep the modal open with the reason.
      if (result && result.count === 0) {
        setError(result.message || 'File uploaded but 0 records were inserted. Check the file columns and the task scope.');
        return;
      }
      onUploadSuccess();
      onClose();
    } catch (err) {
      console.error('Error uploading CSV:', err);
      setError(err.response?.data?.message || err.message || 'Failed to upload CSV');
    } finally {
      setUploading(false);
    }
  };

  if (!isOpen) return null;

  return (
    <div className="modal-overlay" onClick={onClose}>
      <div className="modal-content" onClick={(e) => e.stopPropagation()}>
        <div className="modal-header">
          <h2>Upload Reconciliation CSV</h2>
          <button className="modal-close" onClick={onClose}>×</button>
        </div>
        <form onSubmit={handleSubmit}>
          <div className="modal-body">
            {error && (
              <div className="error-banner">
                <AlertCircle className="icon-sm" />
                {error}
              </div>
            )}
            <div className="form-group">
              <label className="form-label">CSV File *</label>
              <input
                type="file"
                accept=".csv"
                className="form-input"
                onChange={(e) => setFile(e.target.files[0])}
                required
              />
              <span className="form-hint">
                CSV should include reconciliation record data
              </span>
            </div>
          </div>
          <div className="modal-footer">
            <Button variant="secondary" onClick={onClose} type="button" disabled={uploading}>
              Cancel
            </Button>
            <Button type="submit" disabled={uploading || !file}>
              {uploading ? (
                <>
                  <Spinner size="sm" className="icon-sm" />
                  Uploading...
                </>
              ) : (
                <>
                  <Upload className="icon-sm" />
                  Upload
                </>
              )}
            </Button>
          </div>
        </form>
      </div>
    </div>
  );
}

function ReconciliationDeleteModal({ isOpen, onClose, onConfirm, recordCount, deleting }) {
  if (!isOpen) return null;

  return (
    <div className="modal-overlay" onClick={onClose}>
      <div className="modal-content modal-sm" onClick={(e) => e.stopPropagation()}>
        <div className="modal-header">
          <h2>Confirm Delete</h2>
          <button className="modal-close" onClick={onClose}>×</button>
        </div>
        <div className="modal-body">
          <p>
            Are you sure you want to delete {recordCount} record(s)?
          </p>
          <p className="text-muted">This action cannot be undone.</p>
        </div>
        <div className="modal-footer">
          <Button variant="secondary" onClick={onClose} disabled={deleting}>
            Cancel
          </Button>
          <Button onClick={onConfirm} disabled={deleting}>
            {deleting ? (
              <>
                <Spinner size="sm" className="icon-sm" />
                Deleting...
              </>
            ) : (
              <>
                <Trash2 className="icon-sm" />
                Delete Records
              </>
            )}
          </Button>
        </div>
      </div>
    </div>
  );
}

export default TaskReconciliationPage;
